import Switch from "@mui/material/Switch";
import TextInput from "../../../components/TextInput/TextInput";
import SelectInput from "../../../components/SelectInput/SelectInput";
import { ACQUISITION_MODE_OPTIONS, APP_SETTINGS } from "./acquisitionConstants";

const TOGGLE_LABEL_STYLE = {
  color: "#25242e",
  fontSize: "14px",
  fontWeight: 600,
  display: "block",
  marginBottom: "6px",
};

// Shared by the combined Add/Edit Patient form and the standalone
// Acquisition Settings tab in PatientSessionDetails.
const AcquisitionSettingsFields = ({
  formData,
  onChange,
  onCheckboxChange,
  showDivider = true,
  sectioned = false,
}) => {
  const isIntermittent = !!formData.isIntermittentCollection;

  // Backend enum acquisitionSettings.mode: Online = 1, Offline = 2 (None
  // isn't user-selectable here) - usePatientForm converts to/from this
  // "online"/"offline" string for the form.
  const dataMode = formData.dataMode === "offline" ? "offline" : "online";
  const isOffline = dataMode === "offline";

  const handleDataModeChange = (mode) => {
    if (onChange) {
      onChange({ target: { name: "dataMode", value: mode } });
    }
  };

  const handleToggleChange = (e) => {
    if (onCheckboxChange) {
      onCheckboxChange({
        target: {
          name: e.target.name,
          checked: e.target.checked,
        },
      });
    }
  };

  const renderToggle = (name, label, onText, offText) => {
    const checked = !!formData[name];
    return (
      <div
        className="toggle-wrapper"
        style={{ display: "flex", alignItems: "center", gap: "10px", height: "40px" }}
      >
        <Switch
          checked={checked}
          onChange={handleToggleChange}
          name={name}
          color="primary"
          inputProps={{ "aria-label": label }}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: "#104581",
            },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#104581",
            },
          }}
        />
        <span
          style={{
            fontSize: "13.5px",
            fontWeight: 600,
            color: checked ? "#104581" : "#64748b",
          }}
        >
          {checked ? onText : offText}
        </span>
      </div>
    );
  };

  const intermittentToggle = renderToggle(
    "isIntermittentCollection",
    "Intermittent Collection",
    "Enabled",
    "Disabled",
  );

  const dataModeToggle = (
    <div className="data-mode-toggle" role="tablist" aria-label="Data mode">
      <span
        className={`data-mode-toggle_thumb${dataMode === "offline" ? " is-offline" : ""}`}
        aria-hidden="true"
      />
      <button
        type="button"
        role="tab"
        aria-selected={dataMode === "online"}
        className={`data-mode-toggle_option${dataMode === "online" ? " is-active" : ""}`}
        onClick={() => handleDataModeChange("online")}
      >
        Online
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={dataMode === "offline"}
        className={`data-mode-toggle_option${dataMode === "offline" ? " is-active" : ""}`}
        onClick={() => handleDataModeChange("offline")}
      >
        Offline
      </button>
    </div>
  );

  const activeSleepFields = (
    <>
      <div className="patientdetails_container_section_input">
        <TextInput
          title="Active Time (minutes)"
          type="number"
          name="activeTime"
          value={formData.activeTime}
          onChange={onChange}
          placeholder="Enter active time in minutes"
          min={0}
          step={1}
        />
      </div>
      <div className="patientdetails_container_section_input">
        <TextInput
          title="Sleep Time (minutes)"
          type="number"
          name="sleepTime"
          value={formData.sleepTime}
          onChange={onChange}
          placeholder="Enter sleep time in minutes"
          min={0}
          step={1}
        />
      </div>
    </>
  );

  if (sectioned) {
    return (
      <>
        <div className="acquisition-setting-section">
          <h4 className="acquisition-setting-section_title">Data Mode</h4>
          {dataModeToggle}
        </div>

        <div className="acquisition-setting-section">
          <h4 className="acquisition-setting-section_title">Data Acquisition Mode</h4>
          <SelectInput
            name="dataAcquisitionMode"
            value={formData.dataAcquisitionMode}
            onChange={onChange}
            options={ACQUISITION_MODE_OPTIONS}
          />
        </div>

        {isOffline && (
          <div className="acquisition-setting-section">
            <h4 className="acquisition-setting-section_title">Intermittent Collection</h4>
            {intermittentToggle}
            {isIntermittent && (
              <div className="acquisition-setting-section_subfields">{activeSleepFields}</div>
            )}
          </div>
        )}

        {!isOffline &&
          APP_SETTINGS.map(({ name, label }) => (
            <div key={name} className="acquisition-setting-section">
              <h4 className="acquisition-setting-section_title">{label}</h4>
              {renderToggle(name, label, "Yes", "No")}
            </div>
          ))}
      </>
    );
  }

  return (
    <>
      {showDivider && (
        <div className="patientdetails_container_section_divider">
          <h4>Data Acquisition Settings</h4>
        </div>
      )}

      {/* 1. DATA MODE (ONLINE / OFFLINE TOGGLE) */}
      <div className="patientdetails_container_section_input toggle-field">
        <label className="toggle-field_label" style={TOGGLE_LABEL_STYLE}>
          Data Mode
        </label>
        {dataModeToggle}
      </div>

      {/* 2. DATA ACQUISITION MODE (STANDALONE FIELD) */}
      <div className="patientdetails_container_section_input">
        <SelectInput
          title="Data Acquisition Mode"
          name="dataAcquisitionMode"
          value={formData.dataAcquisitionMode}
          onChange={onChange}
          options={ACQUISITION_MODE_OPTIONS}
        />
      </div>

      {/* 3. INTERMITTENT COLLECTION (OFFLINE MODE ONLY, TOGGLE BUTTON SWITCH) */}
      {isOffline && (
        <>
          <div className="patientdetails_container_section_input toggle-field">
            <label className="toggle-field_label" style={TOGGLE_LABEL_STYLE}>
              Intermittent Collection
            </label>
            {intermittentToggle}
          </div>

          {/* 4. CONDITIONALLY SHOW ACTIVE TIME & SLEEP TIME ONLY WHEN TOGGLE IS ENABLED */}
          {isIntermittent && activeSleepFields}
        </>
      )}

      {/* 5. APP SETTINGS (ONLINE MODE ONLY, YES / NO) */}
      {!isOffline &&
        APP_SETTINGS.map(({ name, label }) => (
          <div key={name} className="patientdetails_container_section_input toggle-field">
            <label className="toggle-field_label" style={TOGGLE_LABEL_STYLE}>
              {label}
            </label>
            {renderToggle(name, label, "Yes", "No")}
          </div>
        ))}
    </>
  );
};

export default AcquisitionSettingsFields;
